/**
 * Area contributions — the structural sibling of `objectContributions.ts`: derives the same
 * **softCoverCells** shape terrain/object contributions produce, but from live {@link "./types"
 * AreaEffect} records instead of terrain flags or placed combatants, so `terrainVisibility` can
 * fold all three sources into the SAME closure it hands to the pure `visibility()` (one seam, not
 * a parallel geometry).
 *
 * **Shape-as-truth, materialized on demand.** An `AreaEffect` stores only its template (`origin` +
 * `shape` + `size`); this module is where that template turns into cells, via
 * `space.disk(origin, size)` — the same grid-agnostic geometry `enumerateArea`
 * (`@lancer-vtt/rules`' `spatialQuery.ts`) already uses to enumerate combatants under a
 * Blast/Burst. No per-cell record is ever stored on `EncounterState` — dispersing one cloud never
 * has to "un-paint" cells, and two overlapping clouds never have to reconcile which one a cell
 * belonged to, because neither cloud ever wrote a cell in the first place.
 *
 * **No combatant identity, so no `excludeCoverFrom` (unlike `objectContributions`).** The
 * self-target exclusion exists because an object combatant can itself be the target of an attack
 * and must not gain cover from its own bulk — a relationship that only makes sense for something
 * with a combatant id. An area has no id in that sense (its own `AreaEffectId` is never a valid
 * attack target), so there is nothing to exclude and no parameter for it here. This also means the
 * exclusion mechanism can never *accidentally* suppress area cover: `terrainVisibility` ORs this
 * module's `softCoverCells` in independently of whatever `excludeCoverFrom` strips from
 * `objectContributions`' sets (pinned in `terrain.test.ts`).
 *
 * **Only `soft_cover` areas produce cover cells**, so unlike `ObjectContributions` there is no
 * `hardCoverCells`/`blocksSightCells` here. An `outside_attack_difficulty` dome grants no cover;
 * the attack fold reads it through {@link outsideAttackDifficultyAreas}, and the `in_area`
 * predicate through {@link areaHolds}.
 */
import type { Space } from "@lancer-vtt/space";
import type { AreaEffect, AreaEffectId, Cell } from "./types";

/**
 * The cell-keyed sets `terrainVisibility` ORs into its cover closures. Two fields, because an
 * area's cover reaches the fold through **two different printed rules** that key on different
 * things:
 *
 *  - {@link softCoverCells} — every live area's cells, feeding the **occupancy**
 *    grant (Core p.120: "All characters and objects *within the blast area* benefit
 *    from soft cover"). Unconditional: it doesn't matter who is shooting.
 *  - {@link interveningSoftCoverCells} — the cells of only those areas the **viewer**
 *    (attacker/observer) is *not* standing in, feeding the **line** grant (Core p.66:
 *    "if the line… is broken up by smoke… the target has soft cover"). A cloud you
 *    are inside is not *between* you and anything, so it never intervenes on your own
 *    sightline.
 *
 * When no viewer cells are supplied the two sets are identical.
 */
export type AreaContributions = {
  /** Every live area's cells — the unconditional, target-side **occupancy** grant. */
  softCoverCells: Set<string>;
  /** Cells of areas the viewer is NOT inside — the **intervening** (line) grant only. */
  interveningSoftCoverCells: Set<string>;
}

/** An empty contribution set — the no-op default when no area effects are supplied. */
export function emptyAreaContributions(): AreaContributions {
  return { softCoverCells: new Set(), interveningSoftCoverCells: new Set() };
}

/**
 * Derive the cover contribution of every live area effect in `areaEffects` — the
 * encounter-layer binding `terrainVisibility` folds in. Materializes each record's
 * disk via `space.disk` and adds every cell key to `softCoverCells`; a cell
 * covered by more than one overlapping area is simply added twice into the same
 * `Set` (idempotent), so overlap needs no special-casing here — the caller-side
 * removal of one area (an `area_remove` step deleting one `AreaEffect` from
 * `EncounterState.areaEffects`) is what makes overlap "just work": the surviving
 * area's cells are re-derived fresh on the very next call, untouched by the other
 * area ever having existed.
 *
 * `viewerCells` is the **attacker/observer** footprint (`terrainVisibility`'s `from`).
 * An area containing any of those cells is one the viewer is standing *inside*, so its
 * cells are added to `softCoverCells` (they still cover whoever stands in them) but
 * **withheld** from `interveningSoftCoverCells` — nothing you are inside can be
 * *between* you and your target. Omit it and the two sets are identical.
 *
 * The check is **per-area, not per-cell**: it's the whole cloud you're standing in that
 * stops intervening, not merely the one cell under your feet — the spine walk already
 * skips the endpoints, so a per-cell rule would withhold nothing at all and the cells of
 * your own cloud further along the line would keep granting your target cover.
 */
export function areaContributions(
  space: Space<Cell>,
  areaEffects: Record<AreaEffectId, AreaEffect>,
  viewerCells?: readonly Cell[],
): AreaContributions {
  const out = emptyAreaContributions();
  const viewerKeys = new Set((viewerCells ?? []).map((c) => space.key(c)));
  for (const area of Object.values(areaEffects)) {
    if (area.grants !== "soft_cover") continue;
    // Shape is disk-only (the closed vocabulary on AreaEffect itself), so there is exactly one
    // region shape to materialize.
    const keys = space.disk(area.origin, area.size).cells().map((c) => space.key(c));
    const viewerInside = keys.some((k) => viewerKeys.has(k));
    for (const key of keys) {
      out.softCoverCells.add(key);
      if (!viewerInside) out.interveningSoftCoverCells.add(key);
    }
  }
  return out;
}

/** Whether any of `cells` lies inside `area` (a footprint partly inside counts). */
export function areaHolds(space: Space<Cell>, area: AreaEffect, cells: readonly Cell[]): boolean {
  const disk = space.disk(area.origin, area.size);
  return cells.some((c) => disk.contains(c));
}

/**
 * The live `outside_attack_difficulty` areas that hold the target and not the attacker: each one
 * gives the attack +1 Difficulty (the ENCLAVE-Pattern Support Shield: "any ranged or melee attacks
 * made against characters within the affected area from outside the dome receive +1 Difficulty").
 * An attacker partly inside a dome is not outside it. Read live, like cover, so a target that
 * walks out loses it.
 */
export function outsideAttackDifficultyAreas(
  space: Space<Cell>,
  areaEffects: Record<AreaEffectId, AreaEffect>,
  footprints: { attacker: readonly Cell[]; target: readonly Cell[] },
): AreaEffect[] {
  return Object.values(areaEffects).filter(
    (area) => area.grants === "outside_attack_difficulty" && areaHolds(space, area, footprints.target) && !areaHolds(space, area, footprints.attacker),
  );
}

/**
 * The first live `no_reactions` area holding `c` that `c` did not make (Wandering Nightmare: "affects
 * all characters other than you ... cannot take reactions"), or `undefined`. Read live, so a
 * character that leaves the zone may react again.
 */
export function noReactionsAreaHolding(space: Space<Cell>, areaEffects: Record<AreaEffectId, AreaEffect>, c: { id: string; cells: readonly Cell[] }): AreaEffect | undefined {
  return Object.values(areaEffects).find((area) => area.grants === "no_reactions" && area.source !== c.id && areaHolds(space, area, c.cells));
}
